// How long a lifecycle took, and why it ended that way.
//
// Kevin, 2026-08-24: "can you build how long it takes to process things and the reason for
// it. this can be stored in the database ... there are certain scenarios where it wont give
// an error but we still need to track lifecycle time."
//
// WHY THIS IS NOT errorLog.ts. That records a FAILURE, and only when one was shown. The load
// that produced "Could not load ndb/composite_images.json (network stalled after 3 attempts)"
// was already slow long before it gave up, and the far more common case never errors at all:
// a save that takes 40 seconds and works is invisible, so it becomes "the CPQ is slow" with
// no evidence attached. Every run is recorded here, successful or not.
//
// THE SAME TWO RULES errorLog.ts follows:
//   1. MEASURING MUST NEVER CAUSE A FAILURE. Nothing here throws, nothing is awaited on the
//      user's path, and every call site is fire-and-forget.
//   2. NO SECRETS. Only what the browser already holds.

import { API_BASE } from "./netsuiteClient";
import { getLaunchContext } from "./launchContext";

/** ok = it finished. error = it threw. timeout = it gave up waiting. abandoned = tab closed
 *  mid-flight, which is the case no other record captures: without it, the runs people gave
 *  up on simply vanish and the numbers look better than the experience. */
export type LifecycleOutcome = "ok" | "error" | "timeout" | "abandoned";

export type LifecycleStep = { name: string; ms: number; failed?: boolean; server?: unknown };

export type LifecycleReport = {
  /** 'app-load' | 'save-submit' | 'routings' | ... */
  lifecycle: string;
  outcome: LifecycleOutcome;
  totalMs: number;
  /** Why it ended that way, or what dominated the time. */
  reason?: string | null;
  steps?: LifecycleStep[];
  retries?: number;
  product?: string | null;
  configMode?: string | null;
  itemId?: string | number | null;
  itemName?: string | null;
  fingerprint?: string | null;
  context?: Record<string, unknown>;
};

/** Groups every span from one page load, so a slow save can be read next to the slow load
 *  that preceded it rather than as an unrelated row. */
let runId: string | null = null;
export function lifecycleId(): string {
  if (runId) return runId;
  try {
    const c = globalThis.crypto as Crypto | undefined;
    runId = c && typeof c.randomUUID === "function"
      ? c.randomUUID().slice(0, 18)
      // No crypto (older embedded browsers): time+counter is plenty to group one page load.
      : `r${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
  } catch {
    runId = `r${Date.now().toString(36)}`;
  }
  return runId;
}

function appBuild(): string {
  try {
    const el = Array.from(document.querySelectorAll("script[src]")).find((s) =>
      /assets\/index-.*\.js/.test((s as HTMLScriptElement).src)
    ) as HTMLScriptElement | undefined;
    const m = el ? el.src.match(/assets\/(index-[^/]+\.js)/) : null;
    return m ? m[1] : "unknown";
  } catch {
    return "unknown";
  }
}

/**
 * Record one lifecycle. FIRE AND FORGET — never awaited, never throws, never rejects.
 *
 * sendBeacon first: it is the only transport the browser promises to deliver during unload,
 * which is exactly when an 'abandoned' run is reported. fetch(keepalive) is the fallback.
 */
export function recordLifecycle(report: LifecycleReport): void {
  try {
    if (!report || !report.lifecycle || !Number.isFinite(report.totalMs)) return;
    const ctx = getLaunchContext();
    const body = JSON.stringify({
      ...report,
      totalMs: Math.round(report.totalMs),
      itemId: report.itemId == null ? null : String(report.itemId),
      lifecycleId: lifecycleId(),
      userId: ctx.userId ?? null,
      userName: ctx.userName ?? null,
      appBuild: appBuild(),
      userAgent: typeof navigator === "undefined" ? null : navigator.userAgent,
    });
    const url = `${API_BASE}/timing`;
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      // Content-Type matters: without it the Functions host does not parse req.body as JSON
      // and every field arrives null.
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      /* rule 1 */
    });
  } catch {
    /* rule 1 */
  }
}

/**
 * Measure a lifecycle end to end and record it however it ends.
 *
 * The `finally`-style reporting is the point: a run that throws is exactly the one worth
 * having, and a version that only reported on success would bias every number toward the
 * happy path. The original error is re-thrown untouched, so control flow is identical
 * whether measurement is present or not.
 */
export async function trackLifecycle<T>(
  lifecycle: string,
  fn: (note: (name: string, ms: number) => void) => Promise<T>,
  base: Partial<LifecycleReport> = {}
): Promise<T> {
  const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
  const steps: LifecycleStep[] = [];
  const note = (name: string, ms: number) => { steps.push({ name, ms: Math.round(ms) }); };
  const since = () => (typeof performance !== "undefined" ? performance.now() : Date.now()) - t0;
  try {
    const out = await fn(note);
    recordLifecycle({ ...base, lifecycle, outcome: "ok", totalMs: since(), steps });
    return out;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    // A timeout is worth separating from a generic failure: it means "we waited and gave up",
    // which points at the network rather than at the data or the code.
    const timedOut = /timed out|timeout|stalled|abort/i.test(msg);
    recordLifecycle({
      ...base,
      lifecycle,
      outcome: timedOut ? "timeout" : "error",
      totalMs: since(),
      reason: msg,
      steps,
    });
    throw e;
  }
}

/**
 * Report a lifecycle as abandoned if the tab closes before it finishes.
 *
 * Returns a function to call once it HAS finished. Without this, the runs people gave up
 * waiting for are simply absent, and the remaining numbers describe only the people patient
 * enough to stay.
 */
export function markAbandonedOnUnload(lifecycle: string, base: Partial<LifecycleReport> = {}): () => void {
  if (typeof window === "undefined") return () => {};
  const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
  let done = false;
  const onHide = () => {
    if (done) return;
    done = true;
    const ms = (typeof performance !== "undefined" ? performance.now() : Date.now()) - t0;
    recordLifecycle({ ...base, lifecycle, outcome: "abandoned", totalMs: ms, reason: "the tab was closed or hidden before it finished" });
  };
  // pagehide, not beforeunload: beforeunload does not fire reliably on mobile or when a tab
  // is discarded, and this is precisely the case that must not be missed.
  window.addEventListener("pagehide", onHide, { once: true });
  return () => {
    done = true;
    window.removeEventListener("pagehide", onHide);
  };
}
